import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { filter, Observable, Subject, Subscription, switchMap } from 'rxjs';
import { DropdownBasic } from '../../../ensemble-config/models/dropdown-basic.model';
import { CommonModule } from '@angular/common';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'xpo-prime-autocomplete',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, AutoCompleteModule, MatButtonModule, MatIconModule],
  templateUrl: './xpo-prime-autocomplete.component.html',
  styleUrls: ['./xpo-prime-autocomplete.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => XpoPrimeAutoCompleteComponent),
      multi: true,
    },
  ],
})
export class XpoPrimeAutoCompleteComponent implements ControlValueAccessor, OnDestroy {
  @Input() formGroup: FormGroup;
  @Input() source: (query: string) => Observable<DropdownBasic[]>;
  @Input() selectedObjectToDisplay: DropdownBasic = null;
  @Input() name: string = '';
  @Input() formControlName = '';
  @Input() placeholder: string = '';
  @Input() dropdown: boolean = false;
  @Input() required: boolean = false;
  @Input() showClear: boolean = false;
  @Input() forceSelection: boolean = false;
  @Input() minLength: number = 2;
  @Input() field: string = 'label';
  @Input() uppercase: boolean = false;
  @Input() readonly: boolean = false;
  @Input() searchOnlyByEnterKey: boolean = true;
  @Output() onSelection = new EventEmitter<DropdownBasic>();

  @ViewChild('autoComplete') autoCompleteRef: AutoComplete;

  filteredItems: DropdownBasic[] = [];
  isDisabled: boolean = false;
  isSearching: boolean = false;
  selectFromList: boolean = false;
  disableSearchBtn: boolean = false;
  query: string = '';

  private onChangeFn: (_: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  private searchSubject = new Subject<string>();
  private searchSub: Subscription;

  constructor() {
    this.searchSub = this.searchSubject
      .pipe(
        filter((query) => (query || '').length >= this.minLength),
        switchMap((query) => this.source(query))
      )
      .subscribe({
        next: (result) => {
          this.filteredItems = result;
          this.isSearching = false;
        },
      });
  }

  writeValue(value: any): void {}

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  getControl(): AbstractControl {
    return this.formGroup.get(this.formControlName);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onSelected(item: DropdownBasic) {
    this.getControl().patchValue(item.value || undefined);
    this.selectedObjectToDisplay = item;
    this.selectFromList = true;
    this.onSelection.next(item);
  }

  onSearchInput(event: any) {
    let query: string = event.query || '';
    query = query.trim();
    query = this.uppercase ? query.toUpperCase() : query;

    if (query.length >= this.minLength) {
      this.isSearching = true;
      this.searchSubject.next(query);
    }
  }

  onCleared(): void {
    this.getControl().patchValue(undefined);
    this.searchSubject.next('');
    this.onSelection.next(null);
    this.selectFromList = false;

    if (!this.forceSelection) {
      this.selectedObjectToDisplay = null;
    }
  }

  searchByEnter(event): void {
    event.preventDefault();
    this.autoCompleteRef.search(event, this.getRawInputValue());
  }

  private getRawInputValue(): string {
    return this.autoCompleteRef?.inputEL?.nativeElement?.value || '';
  }

  ngOnDestroy() {
    this.searchSub.unsubscribe();
  }
}
